"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
    initialState?: number;
    hasError?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, initialState, hasError, placeholder, onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [hasValueOnFocus, setHasValueOnFocus] = React.useState(false);

    // Merge refs
    const handleRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        inputRef.current = node;
      },
      [ref]
    );

    const handleFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);

      // Select all text if there's a value
      if (e.target.value) {
        e.target.select();
        setHasValueOnFocus(true);
      }

      // Call original onFocus if provided
      if (onFocus) {
        onFocus(e);
      }
    }, [onFocus]);

    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setHasValueOnFocus(false);

      // Call original onBlur if provided
      if (onBlur) {
        onBlur(e);
      }
    }, [onBlur]);

    // Mobile-specific input mode for better keyboards
    const getInputMode = () => {
      if (props.inputMode) return props.inputMode;
      if (type === 'email') return 'email';
      if (type === 'tel') return 'tel';
      if (type === 'url') return 'url';
      if (type === 'number') return 'numeric';
      return 'text';
    };

    // Mobile enter key hint for better UX
    const getEnterKeyHint = () => {
      if (props.enterKeyHint) return props.enterKeyHint;
      if (type === 'search') return 'search';
      if (type === 'email' || type === 'tel') return 'next';
      return 'done';
    };

    return (
      <input
        type={type}
        inputMode={getInputMode()}
        enterKeyHint={getEnterKeyHint()}
        className={cn(
          // Base styles with form theme variables
          // Mobile: h-12 (48px) for WCAG AA touch-friendly targets (min 44x44px)
          // Desktop: h-10 (40px) for compact design
          "flex h-12 md:h-10 w-full rounded-none px-4 py-3 md:px-3 md:py-1 text-base md:text-sm",
          "bg-form border border-form-border text-form-text placeholder:text-form-placeholder",
          // Simplified mobile: minimal transitions, no shadows
          "transition-colors duration-150",
          // Desktop-only hover effects
          "md:hover:bg-form-hover md:hover:border-form-border-hover",
          // Focus state - clean, minimal
          "focus:ring-1 focus:ring-form-border-focus focus:outline-none focus:bg-form-focus",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Mobile tap highlight color for better touch feedback
          "[ -webkit-tap-highlight-color:rgba(0,0,0,0.1)]",
          hasError && "border-form-error focus:ring-form-error",
          className
        )}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
        ref={handleRef}
      />
    );
  }
);

Input.displayName = "Input";

export { Input };
